import Select, {
  Props as SelectProps,
  SingleValue,
  MultiValue,
  components,
} from "react-select";
import { FaChevronDown } from "react-icons/fa";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";

interface Option {
  value: string | number;
  label: string | number;
}

interface SelectFieldProps<T extends FieldValues> {
  label: string;
  name: Path<T>;
  control: Control<T>;
  options: Option[];
  isMulti?: boolean;
  placeholder?: string;
  error?: string;
  required?: boolean;
  hideErrorMessage?: boolean;
  className?: string;
  menuPlacement?: "auto" | "top" | "bottom";
  rules?: Record<string, any>;
  isDisabled?: boolean;
  removePosition?: boolean;
  removeGrayBg?: boolean;
}

const customStyles = {
  control: (base: any, state: any) => ({
    ...base,
    border: state.isFocused ? "2px solid #EAECF0" : "2px solid #EAECF0",
    boxShadow: state.isFocused ? "none" : "none",
    borderRadius: "10px",
    padding: "4px",
    transition: "border-color 0.2s ease-in-out",
    height: "60px",
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#98A2B3",
    fontSize: "14px",
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "#000",
    fontWeight: "400",
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "#98A2B3",
    fontSize: "20px",
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    zIndex: 10,
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#D1A960"
      : state.isFocused
      ? "#D1A960"
      : "white",
    color: state.isSelected ? "white" : "#111827",
    padding: "10px 12px",
    cursor: "pointer",
    textAlign: "left",
    ":active": {
      backgroundColor: "#D1A960", // Change this to your desired active color
    },
  }),
};

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <FaChevronDown style={{ color: "#98A2B3" }} className="text-base" />{" "}
    </components.DropdownIndicator>
  );
};

function SelectField<T extends FieldValues>({
  label,
  name,
  control,
  options,
  isMulti = false,
  placeholder = "Select...",
  error,
  required = false,
  className = "",
  menuPlacement = "auto",
  rules = {},
  isDisabled = false,
  hideErrorMessage = false,
  removePosition = false,
  removeGrayBg = false,
}: SelectFieldProps<T>) {
  const formattedPlaceholder = placeholder
    ? placeholder.charAt(0).toUpperCase() + placeholder.slice(1).toLowerCase()
    : "";

  return (
    <div
      className={`text-left react-select-wrapper relative ${
        removeGrayBg ? "select_removeGrayBg" : ""
      } ${className} ${error ? "select-field-error" : ""}`}
    >
      <label className="block font-Mulish text-base font-normal text-lightBlue mb-1">
        {/* {label} */}
        {label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()}
        {required && <span className="text-red-500">*</span>}
      </label>
      <Controller
        name={name}
        control={control}
        // rules={required ? { required: `${label} is required` } : {}}
        rules={{
          ...rules,
          required: required ? `${label} is required` : false,
        }}
        render={({ field }) => (
          <Select
            {...field}
            styles={customStyles}
            options={options}
            isMulti={isMulti}
            placeholder={formattedPlaceholder}
            menuPlacement={menuPlacement}
            onChange={(selectedOption) => {
              if (isMulti) {
                const multiValue = selectedOption as MultiValue<Option>;
                field.onChange(multiValue.map((option) => option.value));
              } else {
                const singleValue = selectedOption as SingleValue<Option>;
                field.onChange(singleValue !== null ? singleValue.value : null);
              }
            }}
            value={
              isMulti
                ? options.filter((option) =>
                    Array.isArray(field.value)
                      ? field.value.includes(option.value)
                      : false
                  )
                : options.find((option) => option.value === field.value) || null
            }
            classNamePrefix="react_select selectfield"
            components={{ DropdownIndicator }}
            isDisabled={isDisabled}
          />
        )}
      />
      {!hideErrorMessage && error && (
        <ErrorsMessage
          title={
            error
              ?.replace(/[^\w\s]/gi, "") // Remove special characters
              .toLowerCase()
              .replace(/^\w/, (c) => c.toUpperCase()) ?? // Capitalize only first letter
            ""
          }
          className={`text-right ${
            removePosition ? "" : "absolute"
          }  top-[100%] right-0`}
        />
      )}
    </div>
  );
}

export default SelectField;
